/**
 * Dirigeants — cards éditables + timeline nominations/révocations.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputFR } from "@/components/ui/date-input-fr";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Users, Plus, Pencil, Trash2, UserX, History, Mail, Euro, Save } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  civilite: "M.", prenom: "", nom: "", fonction: "Gérant",
  date_nomination: "", date_revocation: "", date_fin_mandat: "", date_naissance: "",
  lieu_naissance: "", nationalite: "Française", adresse: "",
  email: "", remuneration_annuelle: "", notes: "",
};

const formatDateFR = (iso) => {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

export default function DirigeantsPanel() {
  const [rows, setRows] = useState([]);
  const [fonctions, setFonctions] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [openSheet, setOpenSheet] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const [d, t] = await Promise.all([
        api.get("/legal/dirigeants"),
        api.get("/legal/dirigeants/timeline"),
      ]);
      setRows(d.data.dirigeants || []);
      setFonctions(d.data.fonctions || []);
      setTimeline(t.data.events || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setOpenSheet(true); };
  const openEdit = (r) => {
    setForm({ ...EMPTY, ...r, remuneration_annuelle: r.remuneration_annuelle || "" });
    setEditingId(r.id);
    setOpenSheet(true);
  };

  const save = async () => {
    if (!form.prenom || !form.nom || !form.fonction || !form.date_nomination) {
      toast.error("Prénom, nom, fonction et date de nomination requis"); return;
    }
    const payload = { ...form };
    if (payload.remuneration_annuelle === "" || payload.remuneration_annuelle === null) {
      delete payload.remuneration_annuelle;
    } else {
      payload.remuneration_annuelle = parseFloat(payload.remuneration_annuelle);
    }
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    try {
      if (editingId) {
        await api.patch(`/legal/dirigeants/${editingId}`, payload);
        toast.success("Dirigeant mis à jour");
      } else {
        await api.post("/legal/dirigeants", payload);
        toast.success("Dirigeant ajouté");
      }
      setOpenSheet(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const revoke = async (id) => {
    const d = window.prompt("Date de révocation (JJ/MM/AAAA) ?");
    if (!d) return;
    const parts = d.split("/");
    if (parts.length !== 3) { toast.error("Format attendu JJ/MM/AAAA"); return; }
    const iso = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
    try {
      await api.post(`/legal/dirigeants/${id}/revoke`, { date_revocation: iso });
      toast.success("Dirigeant révoqué"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement ce dirigeant ?")) return;
    try { await api.delete(`/legal/dirigeants/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="dirigeants-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-600">
            <span className="font-medium">{rows.length}</span> dirigeant{rows.length > 1 ? "s" : ""} enregistré{rows.length > 1 ? "s" : ""}
            {" · "}
            <span className="text-emerald-600 font-medium">{rows.filter((r) => r.est_actif).length}</span> actif{rows.filter((r) => r.est_actif).length > 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-dirigeant-btn"><Plus className="w-4 h-4 mr-2" />Ajouter un dirigeant</Button>
      </div>

      {rows.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>Aucun dirigeant enregistré. Ajoutez le Président, le Gérant ou tout autre mandataire social.</p>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <Card key={r.id} className={r.est_actif ? "" : "opacity-60"} data-testid={`dirigeant-card-${r.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{r.civilite || ""} {r.prenom} {r.nom}</p>
                    {r.est_actif
                      ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">En fonction</Badge>
                      : <Badge className="bg-slate-200 text-slate-600 text-xs">Révoqué</Badge>}
                  </div>
                  <p className="text-sm text-blue-700 font-medium mt-0.5">{r.fonction}</p>
                  <div className="mt-2 space-y-1 text-xs text-slate-600">
                    <p>Nommé le : <span className="font-mono">{formatDateFR(r.date_nomination)}</span></p>
                    {r.date_fin_mandat && !r.date_revocation && <p className="text-amber-700">Fin de mandat : <span className="font-mono">{formatDateFR(r.date_fin_mandat)}</span></p>}
                    {r.date_revocation && <p className="text-red-600">Révoqué le : <span className="font-mono">{formatDateFR(r.date_revocation)}</span></p>}
                    {r.email && <p className="flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</p>}
                    {r.remuneration_annuelle && <p className="flex items-center gap-1"><Euro className="w-3 h-3" />{r.remuneration_annuelle.toLocaleString("fr-FR")} € / an</p>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-dirigeant-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                  {r.est_actif && (
                    <Button variant="ghost" size="sm" onClick={() => revoke(r.id)} title="Révoquer" data-testid={`revoke-dirigeant-${r.id}`}>
                      <UserX className="w-4 h-4 text-amber-600" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => del(r.id)} data-testid={`delete-dirigeant-${r.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {timeline.length > 0 && (
        <Card data-testid="dirigeants-timeline">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4" />Historique des nominations & révocations</CardTitle></CardHeader>
          <CardContent>
            <div className="relative pl-6 space-y-3 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
              {timeline.map((e, i) => (
                <div key={i} className="relative">
                  <span className={`absolute -left-4 top-1.5 w-2.5 h-2.5 rounded-full ${e.type === "nomination" ? "bg-emerald-500" : "bg-red-500"}`}></span>
                  <p className="text-xs text-slate-500 font-mono">{formatDateFR(e.date)}</p>
                  <p className="text-sm text-slate-800">{e.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Sheet open={openSheet} onOpenChange={setOpenSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle>{editingId ? "Modifier le dirigeant" : "Ajouter un dirigeant"}</SheetTitle><SheetDescription className="sr-only">Détails du panneau latéral.</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Civilité</Label>
                <Select value={form.civilite} onValueChange={(v) => setForm({ ...form, civilite: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["M.", "Mme"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fonction *</Label>
                <Select value={form.fonction} onValueChange={(v) => setForm({ ...form, fonction: v })}>
                  <SelectTrigger data-testid="dirigeant-fonction-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{fonctions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Prénom *</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="dirigeant-prenom" /></div>
              <div><Label>Nom *</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="dirigeant-nom" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date nomination *</Label><DateInputFR value={form.date_nomination} onChange={(e) => setForm({ ...form, date_nomination: e.target.value })} data-testid="dirigeant-date-nom" /></div>
              <div><Label>Date révocation</Label><DateInputFR value={form.date_revocation} onChange={(e) => setForm({ ...form, date_revocation: e.target.value })} /></div>
              <div><Label>Fin de mandat (alerte J-90/60/30/7)</Label><DateInputFR value={form.date_fin_mandat || ""} onChange={(e) => setForm({ ...form, date_fin_mandat: e.target.value })} data-testid="dirigeant-fin-mandat" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date naissance</Label><DateInputFR value={form.date_naissance} onChange={(e) => setForm({ ...form, date_naissance: e.target.value })} /></div>
              <div><Label>Nationalité</Label><Input value={form.nationalite} onChange={(e) => setForm({ ...form, nationalite: e.target.value })} /></div>
            </div>
            <div><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Rémunération annuelle (€)</Label><Input type="number" value={form.remuneration_annuelle} onChange={(e) => setForm({ ...form, remuneration_annuelle: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <SheetFooter className="mt-4">
            <Button onClick={save} data-testid="save-dirigeant-btn"><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
